/**
 * What a basket is worth at expiry, across spot — ROADMAP 6.7, drawn by 6.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT THE SAME THING AS THE STRATEGY GLYPH
 *
 * `apps/web/.../payoff.ts` draws a 40x20 icon for the strategy picker: strike
 * OFFSETS on the x axis, premium deliberately ignored, no live price needed,
 * correct for every index at once. It is a shape.
 *
 * This is a valuation. Real strikes, real entry prices, real signed
 * quantities, in rupees. The two are kept apart on purpose — one belongs in a
 * list that must not move while it is read, the other is the P&L a trader
 * decides on.
 *
 * EXACT, NOT SAMPLED
 *
 * At expiry the payoff is PIECEWISE LINEAR: every leg's intrinsic value is a
 * straight line with one kink, at its strike. So the whole basket has kinks
 * only at strikes, and between two adjacent strikes it is a straight line.
 *
 * That makes breakevens and extrema solvable rather than searched. A sampled
 * curve finds a breakeven to within one sample and can miss a narrow spike
 * between two samples entirely — and the spike is where an Iron Fly makes its
 * money. Evaluating at the kinks and interpolating inside each segment gives
 * the exact answer with no window to choose and no resolution to tune.
 *
 *     value
 *       │   ╱▔▔▔╲          kinks only here ──┐
 *       │  ╱     ╲                           │
 *     ──┼─╱───────╲──── spot          ▼      ▼
 *       │╱         ╲             ──┬────┬────┬──
 *                                  K1   K2   K3
 *
 * SPOT HAS A FLOOR AND NO CEILING
 *
 * Which makes the two tails genuinely different, not symmetrically
 * "unlimited". A short call's loss really is unbounded: spot can rise without
 * limit. A short put's is not — the worst case is spot at zero, a finite
 * number this module reports. "Unlimited downside" is a figure of speech; the
 * left edge of a payoff chart is a real value, and labelling it unlimited
 * would be wrong at the one point it can be checked.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * What one leg settles at, per unit, for a given spot.
 *
 * A future settles at spot; an option at its intrinsic value. Both then run
 * through the SAME expression as `realisedPnl` — `(settle - entry) x qty` —
 * so the sign is carried by the signed quantity and there is no second path
 * to get a short backwards in.
 */
function legValueAt(leg, spot) {
    const settle = settlementOf(leg, spot);
    if (settle === null)
        return 0;
    return (settle - leg.avgPrice) * leg.netQty;
}
function settlementOf(leg, spot) {
    if (leg.optionType === null)
        return spot;
    // An option without a strike cannot be settled. Contributing 0 would make
    // it look like a leg that expires worthless, which is a different claim.
    if (leg.strike === null || !Number.isFinite(leg.strike))
        return null;
    return leg.optionType === 'CE'
        ? Math.max(0, spot - leg.strike)
        : Math.max(0, leg.strike - spot);
}
/** The basket's P&L at expiry if spot settles exactly here. */
export function payoffAt(legs, spot) {
    if (!Number.isFinite(spot))
        return 0;
    let total = 0;
    for (const leg of legs) {
        if (!Number.isFinite(leg.netQty) || !Number.isFinite(leg.avgPrice))
            continue;
        total += legValueAt(leg, spot);
    }
    return round2(total);
}
/**
 * Rupees of P&L per rupee of spot, in the unbounded tail on one side.
 *
 * Above every strike only calls and futures still respond to spot; below
 * every strike only puts and futures do, and a put's value falls as spot
 * rises, hence the sign flip.
 */
function tailSlope(legs, side) {
    let slope = 0;
    for (const leg of legs) {
        if (!Number.isFinite(leg.netQty))
            continue;
        if (leg.optionType === null) {
            slope += leg.netQty;
            continue;
        }
        if (leg.strike === null || !Number.isFinite(leg.strike))
            continue;
        if (side === 'ABOVE' && leg.optionType === 'CE')
            slope += leg.netQty;
        if (side === 'BELOW' && leg.optionType === 'PE')
            slope -= leg.netQty;
    }
    return slope;
}
/** Every spot at which the curve changes direction, ascending and unique. */
export function kinksOf(legs) {
    const set = new Set();
    for (const leg of legs) {
        if (leg.optionType === null)
            continue;
        if (leg.strike === null || !Number.isFinite(leg.strike))
            continue;
        set.add(leg.strike);
    }
    return [...set].sort((a, b) => a - b);
}
/**
 * Breakevens, solved rather than searched.
 *
 * Inside a segment the curve is a straight line, so a sign change between its
 * endpoints has exactly one root and linear interpolation finds it exactly.
 * The two tails are solved from their slope instead of by extending the
 * window and hoping — an arbitrary window is how a far breakeven goes
 * missing.
 *
 * A root AT a kink is reported once, not once per adjacent segment.
 */
export function breakevens(legs) {
    const kinks = kinksOf(legs);
    const found = [];
    const add = (x) => {
        if (!Number.isFinite(x) || x < 0)
            return;
        const r = round2(x);
        if (!found.some((f) => Math.abs(f - r) < 1e-6))
            found.push(r);
    };
    if (kinks.length === 0) {
        /*
         * Futures only — one straight line over all spot. `payoffAt(0)` plus the
         * slope is enough to solve it, and there is no segment to interpolate in.
         */
        const slope = tailSlope(legs, 'ABOVE');
        if (slope !== 0)
            add(-payoffAt(legs, 0) / slope);
        return found.sort((a, b) => a - b);
    }
    // Half a paisa: `payoffAt` rounds to the paise, so anything inside that is
    // a zero. The same tolerance decides the segment loop below, so a root at a
    // kink is never found twice by two rules that disagree about zero.
    const isZero = (v) => Math.abs(v) < 0.005;
    for (const k of kinks) {
        if (isZero(payoffAt(legs, k)))
            add(k);
    }
    for (let i = 0; i < kinks.length - 1; i += 1) {
        const a = kinks[i];
        const b = kinks[i + 1];
        const fa = payoffAt(legs, a);
        const fb = payoffAt(legs, b);
        if (isZero(fa) || isZero(fb))
            continue; // already added as a kink root
        if (fa > 0 === fb > 0)
            continue;
        add(a + ((b - a) * -fa) / (fb - fa));
    }
    // The tails. f(x) = f(edge) + slope x (x - edge), solved for zero.
    const lo = kinks[0];
    const hi = kinks[kinks.length - 1];
    const slopeBelow = tailSlope(legs, 'BELOW');
    const slopeAbove = tailSlope(legs, 'ABOVE');
    if (slopeBelow !== 0) {
        const root = lo + -payoffAt(legs, lo) / slopeBelow;
        // Only if it actually lands in the tail, and spot cannot be negative.
        if (root < lo && root >= 0)
            add(root);
    }
    if (slopeAbove !== 0) {
        const root = hi + -payoffAt(legs, hi) / slopeAbove;
        if (root > hi)
            add(root);
    }
    return found.sort((a, b) => a - b);
}
/**
 * The shape: extremes, breakevens and both tail slopes.
 *
 * A piecewise-linear function takes its extremes at a kink or in a tail, so
 * the kinks plus spot-zero are the complete candidate set — no search.
 */
export function payoffShape(legs) {
    const kinks = kinksOf(legs);
    const slopeBelow = tailSlope(legs, 'BELOW');
    const slopeAbove = tailSlope(legs, 'ABOVE');
    /*
     * Spot zero is always a candidate because the left tail is BOUNDED — the
     * worst a short put can do is settle against a worthless underlying, which
     * is a finite number and often the real max loss of the basket.
     */
    const candidates = [0, ...kinks];
    let best = { value: payoffAt(legs, candidates[0]), atSpot: candidates[0] };
    let worst = best;
    for (const spot of candidates) {
        const value = payoffAt(legs, spot);
        if (value > best.value)
            best = { value, atSpot: spot };
        if (value < worst.value)
            worst = { value, atSpot: spot };
    }
    const maxProfit = slopeAbove > 0
        ? { kind: 'UNBOUNDED', slope: slopeAbove }
        : { kind: 'FINITE', value: round2(best.value), atSpot: best.atSpot };
    const maxLoss = slopeAbove < 0
        ? { kind: 'UNBOUNDED', slope: slopeAbove }
        : { kind: 'FINITE', value: round2(worst.value), atSpot: worst.atSpot };
    return { maxProfit, maxLoss, breakevens: breakevens(legs), slopeBelow, slopeAbove };
}
/**
 * Samples for drawing, spanning `from`..`to`.
 *
 * EVERY KINK IS INCLUDED whether or not it lands on a sample. A chart drawn
 * from evenly spaced samples alone rounds the corners off the payoff, and on
 * an Iron Fly the corner IS the position — the peak would be drawn lower than
 * the trade can actually make.
 */
export function payoffCurve(legs, opts) {
    // Spot cannot be negative, and the two may arrive the wrong way round.
    const from = Math.max(0, Math.min(opts.from, opts.to));
    const to = Math.max(from, opts.from, opts.to);
    const samples = Math.max(2, Math.min(2000, opts.samples ?? 120));
    const spots = new Set();
    if (to === from) {
        spots.add(from);
    }
    else {
        for (let i = 0; i <= samples; i += 1)
            spots.add(from + ((to - from) * i) / samples);
        for (const k of kinksOf(legs)) {
            if (k >= from && k <= to)
                spots.add(k);
        }
        for (const b of breakevens(legs)) {
            if (b >= from && b <= to)
                spots.add(b);
        }
    }
    return [...spots]
        .sort((a, b) => a - b)
        .map((spot) => ({ spot, value: payoffAt(legs, spot) }));
}
const round2 = (v) => Math.round(v * 100) / 100;
//# sourceMappingURL=basketPayoff.js.map